import React, { useState, useEffect } from 'react'
import Action from './Actions';
import RenderPageNumbers from './renderPageNumbers';
import PrintToPDF from './printToPDF';
import PrintToXLS from './printToXLS';
import { FaEdit } from 'react-icons/fa';
import { FaSave } from 'react-icons/fa';


const DataTable = ({ data, headers, checkboxColumn, onSelectionChange, exportButton, docTitle, noSort, hasMonthSelection, footerValues, fontHeader, fontData, fixedSize, observacoes, customSearchFunction, perPageItems=5, autoWidth=false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filteredAndSortedData, setFilteredAndSortedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsPerPage, setItemsperPage] = useState(perPageItems);
  const [editIndex, setEditIndex] = useState(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const handleSort = () => {
    setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'));
  };
  const currentData = filteredAndSortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((filteredAndSortedData.length || 0) / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder]);

  /**
   * Normaliza string removendo acentos e caracteres especiais
   * @param {string} str - String a ser normalizada
   * @returns {string} - String normalizada
   */
  const normalizeString = (str) => {
    if (!str || typeof str !== 'string') return '';

    return str
      .toLowerCase()
      .normalize('NFD') // Decompõe caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remove marcas diacríticas (acentos)
      .replace(/[^\w\s]/g, '') // Remove caracteres especiais exceto letras, números e espaços
      .replace(/\s+/g, ' ') // Normaliza espaços múltiplos em um único espaço
      .trim(); // Remove espaços no início e fim
  };

  useEffect(() => {
    // Transformar dados das colunas de moeda ANTES de filtrar
    const transformedData = data?.map(item => {
      const newItem = { ...item };
      
      // Verificar se há colunas de moeda para transformar
      headers.forEach(header => {
        if (header.type === 'money' && header.key && newItem[header.key]) {
          let value = newItem[header.key];
          
          // Se já está formatado como moeda brasileira, não mexer
          if (typeof value === 'string' && value.includes('R$')) {
            return;
          }
          
          // Converter valor para número e depois para formato BR
          let numericValue;
          if (typeof value === 'string') {
            // Remove espaços e converte vírgula para ponto se necessário
            numericValue = parseFloat(value.replace(/\s/g, '').replace(',', '.'));
          } else {
            numericValue = parseFloat(value);
          }
          
          // Se é um número válido, formatar como moeda brasileira
          if (!isNaN(numericValue)) {
            newItem[header.key] = new Intl.NumberFormat('pt-BR', { 
              style: 'currency', 
              currency: 'BRL' 
            }).format(numericValue);
          }
        }
      });
      
      return newItem;
    });

    const filteredData = transformedData?.filter((item) => {
      // Se uma função de pesquisa customizada foi fornecida, usa ela
      if (customSearchFunction) {
        return customSearchFunction(item, searchTerm, headers);
      }

      // Normalizar termo de pesquisa
      const normalizedSearchTerm = normalizeString(searchTerm);

      // Se o termo de pesquisa estiver vazio após normalização, mostrar todos os itens
      if (!normalizedSearchTerm) return true;

      // Busca moderna: ignora acentos e caracteres especiais
      return headers.some((header) => {
        const value = item[header.key];
        if (typeof value === 'string') {
          const normalizedValue = normalizeString(value);
          return normalizedValue.includes(normalizedSearchTerm);
        }

        // Para números, converter para string e buscar
        if (typeof value === 'number') {
          const stringValue = value.toString();
          return stringValue.includes(searchTerm);
        }

        return false;
      });
    });
    const sortedData = [...filteredData]?.sort((a, b) => {
      const aValue = (a[headers[0]?.key] || 'nenhum dado cadastrado').toLowerCase();
      const bValue = (b[headers[0]?.key] || 'nenhum dado cadastrado').toLowerCase();

      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    if (noSort) {
      setFilteredAndSortedData(JSON.parse(JSON.stringify(filteredData)));
      return;
    }
    else setFilteredAndSortedData(JSON.parse(JSON.stringify(sortedData)));
  }, [data, searchTerm, sortOrder, headers, customSearchFunction]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleCheckboxChange = (itemId) => {
    onSelectionChange((prevSelectedItems) => {
      if (prevSelectedItems.includes(itemId)) {
        setSelectedItems((prevSelectedItems) => prevSelectedItems.filter((id) => id !== itemId));
        return prevSelectedItems.filter((id) => id !== itemId);
      } else {
        setSelectedItems((prevSelectedItems) => [...prevSelectedItems, itemId]);
        return [...prevSelectedItems, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    onSelectionChange((prevSelectedItems) => {
      // Pega apenas os IDs dos itens visíveis na página atual
      const currentPageItemIds = currentData?.map((item) => item.id) || [];

      // Verifica se todos os itens da página atual estão selecionados
      const allCurrentPageSelected = currentPageItemIds.every(id => prevSelectedItems.includes(id));

      if (allCurrentPageSelected) {
        // Remove apenas os itens da página atual
        const newSelection = prevSelectedItems.filter(id => !currentPageItemIds.includes(id));
        setSelectedItems(newSelection);
        return newSelection;
      } else {
        // Adiciona apenas os itens da página atual que não estão selecionados
        const newSelection = [...new Set([...prevSelectedItems, ...currentPageItemIds])];
        setSelectedItems(newSelection);
        return newSelection;
      }
    });
  };

  const showItem = (item, header) => {
    // O processamento de moeda agora é feito no useEffect acima
    return item;
  }

  const getHeaders = () => {
    let hds = [...headers];
    for (let i = 0; i < hds.length; i++) {
      if (hds[i].actions) {
        delete hds[i];
      }
    }
    return hds;
  }

  const handleTooltipShow = (content, event, force = false) => {
    if (content && (force || content.length > 30)) {
      setTooltipContent(content);
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setShowTooltip(true);
    }
  };

  const handleTooltipHide = () => {
    setShowTooltip(false);
  };
  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Search and Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-vox p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
          {/* Search Input */}
          <div className="relative flex-1 max-w-full sm:max-w-md" data-tour-id="tabela:pesquisar">
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vox-green-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              title="Busca inteligente: ignora acentos, caracteres especiais e diferenças de maiúsculas/minúsculas"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Items Info and Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Total: <span className="text-vox-blue-600 dark:text-vox-green-400 font-semibold">{filteredAndSortedData.length}</span> itens
            </span>

            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <label htmlFor="items-per-page" className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Por página:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => setItemsperPage(Number(e.target.value))}
                className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-vox-green-500 focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={filteredAndSortedData.length}>Todos</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-vox-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          <table className={`w-full min-w-full ${autoWidth ? 'table-auto' : 'table-fixed'}`}>
            <thead className="bg-gradient-to-r from-vox-blue-600 to-vox-blue-800">
              <tr>
                {checkboxColumn && (
                  <th className="w-12 px-3 py-4 text-center text-sm sm:text-base font-medium text-white uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={currentData?.length > 0 && currentData.every(item => selectedItems.includes(item.id))}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-vox-green-600 bg-gray-100 border-gray-300 rounded focus:ring-vox-green-500 focus:ring-2"
                    />
                  </th>
                )}
                {headers?.map((header, index) => (
                  <th key={index} className={`px-3 sm:px-6 py-3 sm:py-4 text-center text-sm sm:text-base font-medium text-white uppercase tracking-wider whitespace-nowrap ${header.width || ''} ${autoWidth ? '' : 'overflow-hidden'}`}>
                    <div className="flex items-center justify-center gap-2">
                      <span
                        className="truncate cursor-help"
                        onMouseEnter={(e) => handleTooltipShow(header?.tooltip || header.label, e, true)}
                        onMouseLeave={handleTooltipHide}
                        title={header?.tooltip || header.label}
                      >
                        {header.label}
                      </span>
                      {header.sortable && (
                        <button
                          className="text-white hover:text-vox-green-300 transition-colors duration-200 focus:outline-none flex-shrink-0"
                          onClick={handleSort}
                        >
                          {sortOrder === 'asc' ?
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg> :
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                          }
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {[...currentData]?.map((item, indexData) => (
                <tr key={item.id || item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200" data-tour-id={indexData === 0 ? 'tabela:primeira-linha' : undefined}>
                  {checkboxColumn && (
                    <td className="w-12 px-3 py-4 text-center whitespace-nowrap">
                      <input
                        type="checkbox"
                        data-tour-id={indexData === 0 ? 'tabela:marcar' : undefined}
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                        className="w-4 h-4 text-vox-green-600 bg-gray-100 border-gray-300 rounded focus:ring-vox-green-500 focus:ring-2"
                      />
                    </td>
                  )}
                  {headers?.map((header, index) => (
                    <td key={index} className={`px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-gray-900 dark:text-gray-100 ${header.wrapText ? 'text-left whitespace-normal break-words' : 'text-center whitespace-nowrap'} ${header.width || ''} ${autoWidth ? '' : 'overflow-hidden'}`}>
                      {header.actions ? (
                        <div className="flex items-center justify-center gap-1" data-tour-id={indexData === 0 ? 'tabela:acoes' : undefined}>
                          {Object.entries(header.actions)?.map(([key, value]) => (
                            <Action key={key} type={key} action={() => value(item.id || item.id)} />
                          ))}
                        </div>
                      ) :
                        header.render ? (
                          <div className={`${header.truncate ? 'max-w-full' : ''}`}>
                            {header.render(item)}
                          </div>
                        ) : header.editable ? (
                          editIndex === indexData ?
                            <div className="flex items-center gap-1 w-full">
                              <input
                                type="text"
                                value={item[header.key] || ''}
                                onChange={(e) => header.editable(e, item, header.key)}
                                className="flex-1 min-w-0 px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-vox-green-500"
                              />
                              <button
                                onClick={() => header.saveEdit()}
                                className="flex-shrink-0 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-green-500"
                                title="Salvar"
                              >
                                <FaSave className="w-3 h-3" />
                              </button>
                            </div>
                            : (
                              <div className="flex items-center justify-between gap-2 w-full">
                                <span className="flex-1 min-w-0 truncate">{item[header.key]}</span>
                                <button
                                  onClick={() => setEditIndex(indexData)}
                                  className="flex-shrink-0 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  title="Editar"
                                >
                                  <FaEdit className="w-3 h-3" />
                                </button>
                              </div>
                            )
                        ) : (
                          <div className={`${header.truncate ? 'max-w-full' : ''}`}>
                            {header.truncate ? (
                              <div
                                className="truncate cursor-help"
                                onMouseEnter={(e) => handleTooltipShow(item[header.key] || '', e)}
                                onMouseLeave={handleTooltipHide}
                              >
                                {showItem(item[header.key], header)}
                              </div>
                            ) : (
                              showItem(item[header.key], header)
                            )}
                          </div>
                        )
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Anterior
            </button>

            <RenderPageNumbers handlePageChange={handlePageChange} totalPages={totalPages} currentPage={currentPage} />

            <button
              onClick={() => setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Próximo
            </button>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      {exportButton && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-4 sm:mt-6" data-tour-id="tabela:exportar">
          <PrintToPDF
            headers={getHeaders()}
            data={filteredAndSortedData}
            docTitle={docTitle}
            hasMonth={hasMonthSelection}
            fixedSize={fixedSize}
            footerValues={footerValues}
            fontHeader={fontHeader}
            fontData={fontData}
            observacoes={observacoes}
          />
          <PrintToXLS
            headers={headers}
            data={filteredAndSortedData}
            docTitle={docTitle}
            hasMonth={hasMonthSelection}
            footerValues={footerValues}
          />
        </div>
      )}

      {/* Custom Tooltip */}
      {showTooltip && (
        <div
          className="fixed z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none"
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 10,
            maxWidth: '300px',
            wordWrap: 'break-word'
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};


export default DataTable;
